import { ApiClient } from './client';
import type { ApiClientOptions } from './client';
import { ApiFailure } from './errors';
import {
  AuditPage,
  BrandingPublished,
  BrandingRead,
  CatalogCategoryRenamed,
  CatalogChanged,
  CatalogOptionChanged,
  CatalogSeeded,
  CatalogUploadMinted,
  CatalogUploadPublished,
  BrandingRolledBack,
  ConciergeChanged,
  ConciergeDisabled,
  ConciergeEnabled,
  ConciergeRead,
  DeliveryPage,
  DomainChanged,
  DomainList,
  ClosureRemoved,
  ClosuresApplied,
  ActivityPage,
  ExecutionDetail,
  ExecutionList,
  FleetHealth,
  FleetPage,
  HoursApplied,
  IntakeCatalogDraft,
  IntakeChanged,
  IntakeDraft,
  IntakeMinted,
  IntakePromoted,
  IntakeRead,
  IntakeSummaryList,
  IntakeUpload,
  IntakeUploads,
  JobStarted,
  KeyIssued,
  KeyList,
  KeyRevoked,
  ModulesChanged,
  Requeued,
  SchedulingPolicySet,
  SettingsSet,
  StatusChanged,
  SupportSessionMinted,
  TenantCatalog,
  TenantCreated,
  TenantDetail,
  TenantHealthRead,
  WebhookList,
  WebhookSet,
} from '../wire';
import type { BrandingDocument, DomainKind, DomainStatus, JobKind, KeyEnv } from '../wire';
import { TenantReadiness } from '../wire';
import { AppLookRead, AppLookSaved, TenantAssetMinted, TenantAssetPublished } from '../wire';
import { BrandingChecked } from '../wire';
import { LoginChanged, LoginInvited, LoginList } from '../wire';
import type { LoginRole } from '../wire';
import type { AgentSettingsPatch, PartnerStatus } from '@yourstore/concierge-client';
import type {
  GuestBooking,
  OrderTiming,
  OrderingPolicyPatch,
  ReservationPayment,
  ShopFulfilment,
  TenantOfferPolicy,
  TenantStatus,
  TillConfig,
} from '@yourstore/domain';

/**
 * The operator surface, as one method per registry row.
 *
 * One per row of `apps/admin-api/src/routes/registry.ts`, in the registry's own
 * order, and **that pairing is the point**. The registry exists so that "what
 * can this service do" is a list somebody can read rather than a grep; a client
 * that assembled URLs at call sites would put half that list back into the
 * views. If a method here has no row there, it is a button that 404s; if a row
 * there has no method here, it is a capability the console cannot reach.
 *
 * There is no method for a client, an appointment or a settlement, because
 * there is no route for one (spec §6.3, "never a data browser"). The console
 * must not even ask — and this file is where "must not ask" is enforceable by
 * reading, since the whole vocabulary of things it can request is here.
 */
export class AdminApi {
  private readonly client: ApiClient;
  /** The same `fetch`, for the one request that is not to `admin-api`: a photograph's PUT. */
  private readonly putImpl: typeof fetch;

  constructor(options: ApiClientOptions) {
    this.client = new ApiClient(options);
    this.putImpl = options.fetchImpl ?? globalThis.fetch.bind(globalThis);
  }

  /* ---- the fleet and its lifecycle ------------------------------- */

  fleet(
    options: {
      limit?: number;
      offset?: number;
      /** One status, or several comma-separated (`onboarding,importing`). */
      status?: string | undefined;
      includeTest?: boolean;
      /** A piece of a business's name or id. */
      search?: string | undefined;
    } = {},
  ): Promise<FleetPage> {
    return this.client.request({
      method: 'GET',
      path: '/tenants',
      query: {
        limit: options.limit,
        offset: options.offset,
        status: options.status,
        include_test: options.includeTest === true ? 'true' : undefined,
        q: options.search === undefined || options.search === '' ? undefined : options.search,
      },
      schema: FleetPage,
    });
  }

  createTenant(body: {
    tenant_id: string;
    name: string;
    timezone: string;
    currency: string;
    status?: string;
    plan?: string;
  }): Promise<TenantCreated> {
    return this.client.request({
      method: 'POST',
      path: '/tenants',
      body,
      schema: TenantCreated,
    });
  }

  tenant(tenantId: string): Promise<TenantDetail> {
    return this.client.request({
      method: 'GET',
      path: `/tenants/${encodeURIComponent(tenantId)}`,
      schema: TenantDetail,
    });
  }

  /**
   * Health for the page of tenants on screen — the ids the fleet listing just
   * returned, and no more. The server refuses a list longer than a page rather
   * than trimming it, so the column never goes silently blank for the last
   * few rows.
   */
  fleetHealth(tenantIds: readonly string[]): Promise<FleetHealth> {
    return this.client.request({
      method: 'GET',
      path: '/fleet/health',
      query: { tenants: tenantIds.join(',') },
      schema: FleetHealth,
    });
  }

  tenantHealth(tenantId: string): Promise<TenantHealthRead> {
    return this.client.request({
      method: 'GET',
      path: `/tenants/${encodeURIComponent(tenantId)}/health`,
      schema: TenantHealthRead,
    });
  }

  /** The tenant's trail, newest first, paged by id: `before` is the last `audit_id` seen. */
  activity(
    tenantId: string,
    options: {
      before?: string | undefined;
      limit?: number | undefined;
      /** The business's own people, or ours; absent is everyone. */
      who?: 'business' | 'operators' | undefined;
      /** Leave out the rows that are only somebody looking. */
      changesOnly?: boolean | undefined;
    } = {},
  ): Promise<ActivityPage> {
    return this.client.request({
      method: 'GET',
      path: `/tenants/${encodeURIComponent(tenantId)}/activity`,
      query: {
        before: options.before,
        limit: options.limit,
        who: options.who,
        changes_only: options.changesOnly === true ? 'true' : undefined,
      },
      schema: ActivityPage,
    });
  }

  setStatus(tenantId: string, status: TenantStatus): Promise<StatusChanged> {
    return this.client.request({
      method: 'PUT',
      path: `/tenants/${encodeURIComponent(tenantId)}/status`,
      body: { status },
      schema: StatusChanged,
    });
  }

  /**
   * **"See what she sees"** — a fifteen-minute, read-only staff seat for this
   * tenant, minted after its audit row is written (spec §6.3: an operator
   * viewing tenant data is an audited action, and this is the most direct
   * view the console offers). The answer's `token` is a credential for
   * `core-api`; the console's only job with it is to open the staff app with
   * it in the URL *fragment* and hold no copy.
   */
  mintSupportSession(tenantId: string): Promise<SupportSessionMinted> {
    return this.client.request({
      method: 'POST',
      path: `/tenants/${encodeURIComponent(tenantId)}/support-session`,
      schema: SupportSessionMinted,
    });
  }

  /**
   * Enable, disable and trial in **one** call.
   *
   * The server validates dependencies against the state the request would leave
   * behind, which is why `clients, services, calendar, partner_api` succeeds as
   * one request and would be refused as four. The console therefore sends what
   * the operator ticked, not one request per tick.
   */
  setModules(
    tenantId: string,
    change: { enable?: string[]; disable?: string[]; trial?: string[] },
  ): Promise<ModulesChanged> {
    return this.client.request({
      method: 'PUT',
      path: `/tenants/${encodeURIComponent(tenantId)}/modules`,
      body: {
        ...(change.enable === undefined || change.enable.length === 0
          ? {}
          : { enable: change.enable }),
        ...(change.disable === undefined || change.disable.length === 0
          ? {}
          : { disable: change.disable }),
        ...(change.trial === undefined || change.trial.length === 0 ? {} : { trial: change.trial }),
      },
      schema: ModulesChanged,
    });
  }

  /**
   * **The house's own offer rules** (`docs/lovespa-gaps-plan.md` A2–A4): the
   * default gap between guests, the minimum lead time and the same-day cut-off,
   * and the offer policy the simulator was used to choose.
   *
   * There is no matching read and no simulate call, and both absences are the
   * design. The read rides `tenant()`, which already carries
   * `tenant_config.config`; and the simulation happens **in this bundle**, on a
   * day an operator types in beside the owner, because `admin_api` holds no
   * privilege on `appointments`, `resources` or `items` (migration 0008) and
   * must not. `OfferSimulator.tsx` runs `@yourstore/scheduling` — the real
   * engine — against a synthetic context, and nothing crosses the wire until
   * this method.
   *
   * `null` **removes** a key rather than setting it to a zero value, because
   * "this tenant has no offer policy" and "this tenant's offer policy is open"
   * are different facts and the readers in `@yourstore/domain` tell them apart.
   */
  setSchedulingPolicy(
    tenantId: string,
    body: {
      default_buffer_before_min?: number | null;
      default_buffer_after_min?: number | null;
      min_lead_minutes?: number | null;
      same_day_cutoff?: string | null;
      offer_policy?: TenantOfferPolicy | null;
    },
  ): Promise<SchedulingPolicySet> {
    return this.client.request({
      method: 'PUT',
      path: `/tenants/${encodeURIComponent(tenantId)}/scheduling-policy`,
      body,
      schema: SchedulingPolicySet,
    });
  }

  /**
   * **What this business's counter does** (`docs/lovespa-gaps-plan.md` B4/C1,
   * `docs/lovespa-discovery.md` §2.3, spec §3.6): which tenders the till draws,
   * whether the shop is collected or posted, where a collection is paid for,
   * how long the shelf waits, when takeaway orders are for, and whether the
   * website books or asks.
   *
   * Six keys and one call, because they are one form and one transaction — six
   * routes would be six read-modify-writes against one JSONB document, and a
   * failure halfway would leave a business configured with half an answer.
   * There is no matching read, for `setSchedulingPolicy`'s reason: `tenant()`
   * already carries `tenant_config.config`, and a second way to learn the same
   * fact is a second thing to keep in step.
   *
   * `null` **removes** a key rather than writing a default in its place, and on
   * two of these six that is the interesting move: a tenant with no
   * `shop_payment` resolves to `prepay` or `at_pickup` depending on whether it
   * has a usable payment rail, so clearing the key is a live arrangement rather
   * than a gap. The console therefore sends only what the operator changed.
   */
  setSettings(
    tenantId: string,
    body: {
      till?: TillConfig | null;
      shop_fulfilment?: ShopFulfilment | null;
      shop_payment?: ReservationPayment | null;
      shop_pickup_expiry_days?: number | null;
      order_timing?: OrderTiming | null;
      guest_booking?: GuestBooking | null;
      request_hold_hours?: number | null;
      /** The kitchen's own controls (V1), one block; a field `null` clears it. */
      ordering?: OrderingPolicyPatch | null;
      /** Who may change what the business sells (B10); `null` is back to the owner. */
      catalog_editors?: 'owner' | 'owner_and_staff' | null;
    },
  ): Promise<SettingsSet> {
    return this.client.request({
      method: 'PUT',
      path: `/tenants/${encodeURIComponent(tenantId)}/settings`,
      body,
      schema: SettingsSet,
    });
  }

  /* ---- keys ------------------------------------------------------ */

  keys(tenantId: string, includeRevoked = false): Promise<KeyList> {
    return this.client.request({
      method: 'GET',
      path: `/tenants/${encodeURIComponent(tenantId)}/keys`,
      query: includeRevoked ? { all: '1' } : {},
      schema: KeyList,
    });
  }

  /** The one response that carries a live credential. It is never stored. */
  issueKey(
    tenantId: string,
    body: { integration: string; env: KeyEnv; scopes: string[] },
  ): Promise<KeyIssued> {
    return this.client.request({
      method: 'POST',
      path: `/tenants/${encodeURIComponent(tenantId)}/keys`,
      body,
      schema: KeyIssued,
    });
  }

  revokeKey(tenantId: string, keyId: string): Promise<KeyRevoked> {
    return this.client.request({
      method: 'POST',
      path: `/tenants/${encodeURIComponent(tenantId)}/keys/${encodeURIComponent(keyId)}/revoke`,
      schema: KeyRevoked,
    });
  }

  /* ---- webhooks -------------------------------------------------- */

  webhooks(tenantId: string): Promise<WebhookList> {
    return this.client.request({
      method: 'GET',
      path: `/tenants/${encodeURIComponent(tenantId)}/webhooks`,
      schema: WebhookList,
    });
  }

  /**
   * The destination and the **reference** to its signing secret.
   *
   * `secret_ref` is a Secret Manager resource name. The secret itself is never a
   * request field here for the same reason it is never a column: it would land
   * in a browser's network log, a proxy's access log and the service's request
   * body, three places a shell history is not. The server refuses a value that
   * looks like a secret, and the form says so before the click.
   */
  setWebhook(
    tenantId: string,
    body: {
      integration?: string;
      url: string;
      secret_ref: string;
      signature_header?: string;
      event_types?: string[];
      active?: boolean;
    },
  ): Promise<WebhookSet> {
    return this.client.request({
      method: 'PUT',
      path: `/tenants/${encodeURIComponent(tenantId)}/webhooks`,
      body,
      schema: WebhookSet,
    });
  }

  deliveries(
    tenantId: string,
    options: { limit?: number; offset?: number; status?: string } = {},
  ): Promise<DeliveryPage> {
    return this.client.request({
      method: 'GET',
      path: `/tenants/${encodeURIComponent(tenantId)}/deliveries`,
      query: { limit: options.limit, offset: options.offset, status: options.status },
      schema: DeliveryPage,
    });
  }

  /** Replay every event since a timestamp (spec §5.6.2). `since` is required. */
  requeue(tenantId: string, since: string): Promise<Requeued> {
    return this.client.request({
      method: 'POST',
      path: `/tenants/${encodeURIComponent(tenantId)}/deliveries/requeue`,
      body: { since },
      schema: Requeued,
    });
  }

  /* ---- domains (spec §6.4.2) ------------------------------------- */

  domains(tenantId: string): Promise<DomainList> {
    return this.client.request({
      method: 'GET',
      path: `/tenants/${encodeURIComponent(tenantId)}/domains`,
      schema: DomainList,
    });
  }

  /**
   * Point a hostname at a tenant. Lands at `pending_dns` unless told otherwise.
   *
   * The console cannot move a hostname another tenant holds and cannot remove
   * one: `admin-api` answers `409` for the first and has no route for the
   * second, because both take a working booking site off the air and sign out
   * every guest whose session cookie is scoped to it (spec §6.4.1). Those are
   * `apps/cli`'s, where the hostname is re-typed. That is why there is no
   * `removeDomain` here — the absence is the design, not an omission.
   */
  addDomain(
    tenantId: string,
    body: { hostname: string; kind?: DomainKind; status?: DomainStatus },
  ): Promise<DomainChanged> {
    return this.client.request({
      method: 'POST',
      path: `/tenants/${encodeURIComponent(tenantId)}/domains`,
      body,
      schema: DomainChanged,
    });
  }

  setDomainStatus(
    tenantId: string,
    hostname: string,
    status: DomainStatus,
  ): Promise<DomainChanged> {
    return this.client.request({
      method: 'PUT',
      path:
        `/tenants/${encodeURIComponent(tenantId)}/domains/` +
        `${encodeURIComponent(hostname)}/status`,
      body: { status },
      schema: DomainChanged,
    });
  }

  /* ---- branding: the theme editor (spec §6.4.3) ------------------- */

  /**
   * The tenant's theme **and the contract vocabulary** the editor renders from.
   *
   * The vocabulary rides the read rather than being a constant in this bundle:
   * one duplicate of the site's token table exists (in `admin-api`) and this
   * console reads it over the wire instead of making a second.
   */
  branding(tenantId: string): Promise<BrandingRead> {
    return this.client.request({
      method: 'GET',
      path: `/tenants/${encodeURIComponent(tenantId)}/branding`,
      schema: BrandingRead,
    });
  }

  /**
   * Publish. The server validates against contract v1, **sanitizes every content
   * zone**, stores only the sanitized bytes and bumps `branding_version`.
   *
   * The response carries the stored document back, and the editor renders *that*
   * rather than what was typed — which is how an operator learns what survives
   * sanitization: by seeing it, on their own words, immediately.
   */
  publishBranding(
    tenantId: string,
    branding: BrandingDocument,
    note?: string,
    /**
     * The staff PWA's named set (`docs/lovespa-personality.md`).
     *
     * **Always sent by this console, even when it is empty** — `{}` is how the
     * staff tab is cleared, and omitting the key means "leave whatever is
     * stored alone". The console holds both tabs on one screen with one Publish
     * button, so it always knows what the staff document should be and always
     * says so; the *server's* three-way handling exists for a caller that does
     * not, such as a script publishing only a guest theme.
     */
    brandingStaff?: Record<string, string>,
  ): Promise<BrandingPublished> {
    return this.client.request({
      method: 'PUT',
      path: `/tenants/${encodeURIComponent(tenantId)}/branding`,
      body: {
        branding,
        ...(brandingStaff === undefined ? {} : { branding_staff: brandingStaff }),
        ...(note === undefined || note === '' ? {} : { note }),
      },
      schema: BrandingPublished,
    });
  }

  /** Restore a kept version's document — as a new, higher version. */
  rollbackBranding(tenantId: string, toVersion?: number): Promise<BrandingRolledBack> {
    return this.client.request({
      method: 'POST',
      path: `/tenants/${encodeURIComponent(tenantId)}/branding/rollback`,
      body: toVersion === undefined ? {} : { to_version: toVersion },
      schema: BrandingRolledBack,
    });
  }

  /**
   * The publish's validator over a document, with nothing stored — the page
   * builder asks it before offering Publish (console redesign C2).
   */
  checkBranding(tenantId: string, branding: BrandingDocument): Promise<BrandingChecked> {
    return this.client.request({
      method: 'POST',
      path: `/tenants/${encodeURIComponent(tenantId)}/branding/check`,
      body: { branding },
      schema: BrandingChecked,
    });
  }

  /* ---- the concierge integration --------------------------------- */

  /**
   * Is it on, is the provider connected, what is synced, what is missing.
   *
   * Answers `200` with `configured: false` on a deployment that has no
   * integration — never a `404`, and never an empty card. The section renders
   * one sentence and no button, which is the whole of "honestly off".
   */
  concierge(tenantId: string): Promise<ConciergeRead> {
    return this.client.request({
      method: 'GET',
      path: `/tenants/${encodeURIComponent(tenantId)}/concierge`,
      schema: ConciergeRead,
    });
  }

  /**
   * **The one action, and the reason this method has no interesting body.**
   *
   * Modules, a key, a signing secret, the partner `PUT`, the webhook endpoint
   * and the site key are one server-side sequence (the provisioning doc §9).
   * Nothing about it is a parameter, because nothing about it is a choice a
   * console should be making: the scopes are the agreement's, the environment
   * is the deployment's, and the origins are the tenant's own live hostnames.
   *
   * There is no `plaintext` in the answer and no `OnceBox` to put one in. That
   * absence is the feature.
   */
  enableConcierge(tenantId: string): Promise<ConciergeEnabled> {
    return this.client.request({
      method: 'POST',
      path: `/tenants/${encodeURIComponent(tenantId)}/concierge/enable`,
      body: {},
      schema: ConciergeEnabled,
    });
  }

  /** Pause there, revoke the key, switch `partner_api` off. Deletes nothing. */
  disableConcierge(tenantId: string): Promise<ConciergeDisabled> {
    return this.client.request({
      method: 'POST',
      path: `/tenants/${encodeURIComponent(tenantId)}/concierge/disable`,
      body: {},
      schema: ConciergeDisabled,
    });
  }

  /**
   * Tune the agent. **Partial — only changed fields** — and the type is the
   * package's `.strict()` one, so a field that is not the agent's does not
   * compile here and is refused by the server if it somehow arrives.
   */
  conciergeSettings(tenantId: string, patch: AgentSettingsPatch): Promise<ConciergeChanged> {
    return this.client.request({
      method: 'PATCH',
      path: `/tenants/${encodeURIComponent(tenantId)}/concierge/settings`,
      body: patch,
      schema: ConciergeChanged,
    });
  }

  conciergeSync(tenantId: string): Promise<ConciergeChanged> {
    return this.client.request({
      method: 'POST',
      path: `/tenants/${encodeURIComponent(tenantId)}/concierge/sync`,
      body: {},
      schema: ConciergeChanged,
    });
  }

  conciergeStatus(tenantId: string, status: PartnerStatus): Promise<ConciergeChanged> {
    return this.client.request({
      method: 'POST',
      path: `/tenants/${encodeURIComponent(tenantId)}/concierge/status`,
      body: { status },
      schema: ConciergeChanged,
    });
  }

  /* ---- the tenant intake (docs/tenant-intake-design.md) ---------- */

  /**
   * Every live intake, as counts — the fleet's chip.
   *
   * Cross-tenant, like the fleet listing itself, and it carries no answers.
   */
  intakes(): Promise<IntakeSummaryList> {
    return this.client.request({ method: 'GET', path: '/intakes', schema: IntakeSummaryList });
  }

  /**
   * Mint the owner's link, or rotate the one that exists.
   *
   * **Re-minting kills the old link.** The server keeps the answers and changes
   * the digest, so the message an owner already has stops working the instant
   * this returns — which the confirm dialog says before the click, because it
   * is the one consequence an operator cannot undo by clicking again.
   *
   * `code` and `link` are in the answer and nowhere else. They go into an
   * `OnceBox`.
   */
  mintIntake(
    tenantId: string,
    body: { template: string; locale: string; ttl_days?: number },
  ): Promise<IntakeMinted> {
    return this.client.request({
      method: 'POST',
      path: `/tenants/${encodeURIComponent(tenantId)}/intake`,
      body,
      schema: IntakeMinted,
    });
  }

  /**
   * The answers, and the server-computed review projection beside them.
   *
   * A `404` is an ordinary state — most tenants have no intake — and the
   * section renders "not sent yet" from it, the way the Concierge card renders
   * `configured: false`.
   */
  intake(tenantId: string): Promise<IntakeRead> {
    return this.client.request({
      method: 'GET',
      path: `/tenants/${encodeURIComponent(tenantId)}/intake`,
      schema: IntakeRead,
    });
  }

  reopenIntake(tenantId: string): Promise<IntakeChanged> {
    return this.client.request({
      method: 'POST',
      path: `/tenants/${encodeURIComponent(tenantId)}/intake/reopen`,
      body: {},
      schema: IntakeChanged,
    });
  }

  closeIntake(tenantId: string): Promise<IntakeChanged> {
    return this.client.request({
      method: 'POST',
      path: `/tenants/${encodeURIComponent(tenantId)}/intake/close`,
      body: {},
      schema: IntakeChanged,
    });
  }

  /** `days` is counted from now, not from the old expiry. */
  extendIntake(tenantId: string, days: number): Promise<IntakeChanged> {
    return this.client.request({
      method: 'POST',
      path: `/tenants/${encodeURIComponent(tenantId)}/intake/extend`,
      body: { days },
      schema: IntakeChanged,
    });
  }

  /**
   * The template's document with the owner's words in it — **a read**.
   *
   * It writes nothing (D5). The screen hands `document` to the branding editor
   * through the same door a template uses, and shows `warnings` and `unplaced`
   * beside it so the operator can paste the leftovers by hand.
   */
  intakeDraft(tenantId: string, template?: string, skip?: string[]): Promise<IntakeDraft> {
    return this.client.request({
      method: 'GET',
      path: `/tenants/${encodeURIComponent(tenantId)}/intake/draft`,
      query: {
        template,
        skip: skip === undefined || skip.length === 0 ? undefined : skip.join(','),
      },
      schema: IntakeDraft,
    });
  }

  /**
   * The owner's menu as a catalog seed file — **also a read**.
   *
   * **That sentence used to end here**, and it read: "`admin_api` holds no
   * privilege on the catalog at all, so there is no verb here that could write
   * one even if somebody wanted it." Migration 0050 changed the privilege, on an
   * argument about what a catalog *is*, and `seedCatalog` below is the verb. The
   * draft is still read-only and still the same document — what changed is that
   * the operator can hand it straight to the seeder instead of saving a file and
   * opening a terminal, which is the loop GS4 exists to close.
   */
  intakeCatalogDraft(tenantId: string): Promise<IntakeCatalogDraft> {
    return this.client.request({
      method: 'GET',
      path: `/tenants/${encodeURIComponent(tenantId)}/intake/catalog-draft`,
      schema: IntakeCatalogDraft,
    });
  }

  /**
   * The two writes that reach a tenant's **book** (D6).
   *
   * Both are refused by the database once a tenant is `live` — a `BEFORE
   * UPDATE` trigger for the hours, a RESTRICTIVE policy for the closures — and
   * the refusal arrives as a `403` with the store's own sentence. The console
   * disables the buttons with the reason as well, but the disabling is a
   * courtesy and the database is the control.
   */
  applyIntakeHours(tenantId: string): Promise<HoursApplied> {
    return this.client.request({
      method: 'POST',
      path: `/tenants/${encodeURIComponent(tenantId)}/intake/apply-hours`,
      body: {},
      schema: HoursApplied,
    });
  }

  applyIntakeClosures(tenantId: string): Promise<ClosuresApplied> {
    return this.client.request({
      method: 'POST',
      path: `/tenants/${encodeURIComponent(tenantId)}/intake/apply-closures`,
      body: {},
      schema: ClosuresApplied,
    });
  }

  removeIntakeClosure(tenantId: string, blackoutId: string): Promise<ClosureRemoved> {
    return this.client.request({
      method: 'DELETE',
      path:
        `/tenants/${encodeURIComponent(tenantId)}/intake/closures/` +
        `${encodeURIComponent(blackoutId)}`,
      schema: ClosureRemoved,
    });
  }

  /** The owner's photographs, with a short-lived signed read for each. */
  intakeUploads(tenantId: string): Promise<IntakeUploads> {
    return this.client.request({
      method: 'GET',
      path: `/tenants/${encodeURIComponent(tenantId)}/intake/uploads`,
      schema: IntakeUploads,
    });
  }

  /** Copy one into the public bucket, under a name that is never an overwrite. */
  promoteIntakeUpload(tenantId: string, uploadId: string): Promise<IntakePromoted> {
    return this.client.request({
      method: 'POST',
      path:
        `/tenants/${encodeURIComponent(tenantId)}/intake/uploads/` +
        `${encodeURIComponent(uploadId)}/promote`,
      body: {},
      schema: IntakePromoted,
    });
  }

  rejectIntakeUpload(tenantId: string, uploadId: string): Promise<IntakeUpload> {
    return this.client.request({
      method: 'POST',
      path:
        `/tenants/${encodeURIComponent(tenantId)}/intake/uploads/` +
        `${encodeURIComponent(uploadId)}/reject`,
      body: {},
      schema: IntakeUpload,
    });
  }

  /* ---- the audit log --------------------------------------------- */

  audit(
    options: {
      tenant_id?: string;
      action?: string;
      actor?: string;
      /** A piece of the actor — "jose" finds `operator:jose@…`. */
      person?: string;
      /** Leave out the rows that are only somebody looking. */
      changes_only?: boolean;
      since?: string;
      limit?: number;
      offset?: number;
    } = {},
  ): Promise<AuditPage> {
    return this.client.request({
      method: 'GET',
      path: '/audit',
      query: {
        tenant_id: options.tenant_id,
        action: options.action,
        actor: options.actor,
        person: options.person,
        changes_only: options.changes_only === true ? 'true' : undefined,
        since: options.since,
        limit: options.limit,
        offset: options.offset,
      },
      schema: AuditPage,
    });
  }

  /* ---- jobs ------------------------------------------------------ */

  runJob(
    job: JobKind,
    body: { tenant_id: string; execute?: boolean; file?: string; base_url?: string },
  ): Promise<JobStarted> {
    return this.client.request({
      method: 'POST',
      path: `/jobs/${job}/run`,
      body,
      schema: JobStarted,
    });
  }

  /* ---- the catalog (GS4, migration 0050) ------------------------- */

  /**
   * A tenant's sellables, for **editing**.
   *
   * Localized fields arrive as maps rather than resolved strings, and retired
   * items arrive with the live ones. Both are deliberate and both are the
   * difference between a rendering surface and an authoring one: an operator who
   * cannot see the Spanish will overwrite it, and an item they cannot see is an
   * item they cannot switch back on.
   */
  catalog(tenantId: string): Promise<TenantCatalog> {
    return this.client.request({
      method: 'GET',
      path: `/tenants/${encodeURIComponent(tenantId)}/catalog`,
      schema: TenantCatalog,
    });
  }

  /**
   * How an item reads — its blurb, its heading, where it sits, a product's brand
   * line. Allowed at **any** status, which is the whole of GS4: a tenant who has
   * been live for a year still has a photo shoot.
   */
  setCatalogPresentation(
    tenantId: string,
    itemId: string,
    body: {
      description?: string | Record<string, string>;
      category?: string | Record<string, string> | null;
      sort_order?: number;
      brand?: string;
      notes?: string;
      /** A product's "Show on the website" (B10). Presentation: allowed at any status. */
      on_website?: boolean;
    },
  ): Promise<CatalogChanged> {
    return this.client.request({
      method: 'PATCH',
      path: `/tenants/${encodeURIComponent(tenantId)}/catalog/items/${encodeURIComponent(itemId)}/presentation`,
      body,
      schema: CatalogChanged,
    });
  }

  /**
   * What a thing is called, what it costs, how long it takes, whether it is
   * still sold.
   *
   * **Refused by the database once a tenant is live**, with a `403` carrying the
   * trigger's own sentence. The console disables the fields as well and says
   * why, but the disabling is a courtesy: the fence is migration 0050's and this
   * client tests no status.
   */
  setCatalogCommerce(
    tenantId: string,
    itemId: string,
    body: {
      name?: string | Record<string, string>;
      price_cents?: number;
      active?: boolean;
      duration_min?: number;
      /** `null` hands the gap back to the house default; `0` is none (0064). */
      buffer_before_min?: number | null;
      buffer_after_min?: number | null;
      max_party?: number;
      bookable_by_partner?: boolean;
      bookable_online?: boolean;
      /**
       * A **patch** field and never a create one (design §8.1): a service
       * created attach-only with nothing linked to it is a row nobody can book
       * by any route, so it is turned on afterwards, once somebody has said what
       * it attaches to.
       */
      attach_only?: boolean;
    },
  ): Promise<CatalogChanged> {
    return this.client.request({
      method: 'PATCH',
      path: `/tenants/${encodeURIComponent(tenantId)}/catalog/items/${encodeURIComponent(itemId)}/commerce`,
      body,
      schema: CatalogChanged,
    });
  }

  /** Add a service or a shelf product. Onboarding only, by the database. */
  createCatalogItem(
    tenantId: string,
    body: {
      kind: 'scheduled' | 'stocked';
      name: string;
      description?: string;
      price_cents: number;
      duration_min?: number;
      sort_order?: number;
    },
  ): Promise<CatalogChanged> {
    return this.client.request({
      method: 'POST',
      path: `/tenants/${encodeURIComponent(tenantId)}/catalog/items`,
      body,
      schema: CatalogChanged,
    });
  }

  /**
   * An item's photographs, **in order and wholesale**.
   *
   * Attach, reorder, remove and re-word alt text are one call, because
   * `repos/media.ts` replaces an item's media as a set: the array is the
   * statement of which photographs this item has, and positions are assigned
   * from it. There is nothing to renumber and no partial state to get wrong.
   */
  setCatalogMedia(
    tenantId: string,
    itemId: string,
    images: readonly {
      url: string;
      alt: string | Record<string, string>;
      /** On the website (B10); absent keeps what the photograph with this URL said. */
      on_website?: boolean;
    }[],
  ): Promise<CatalogChanged> {
    return this.client.request({
      method: 'PUT',
      path: `/tenants/${encodeURIComponent(tenantId)}/catalog/items/${encodeURIComponent(itemId)}/media`,
      body: { images },
      schema: CatalogChanged,
    });
  }

  /**
   * Rename a heading everywhere it appears.
   *
   * A category is a **column on the item** rather than a row (migration 0024
   * decision 1), so renaming one is an edit to every item filed under it — and
   * doing that one item at a time is how a tenant ends up with two category
   * pages differing by a comma.
   */
  renameCatalogCategory(
    tenantId: string,
    body: { from: string; to: string | null },
  ): Promise<CatalogCategoryRenamed> {
    return this.client.request({
      method: 'PUT',
      path: `/tenants/${encodeURIComponent(tenantId)}/catalog/categories`,
      body,
      schema: CatalogCategoryRenamed,
    });
  }

  /**
   * Seed from a catalog file — the CLI's document, through the CLI's parser
   * (`@yourstore/catalog-file`).
   *
   * `apply` defaults to false and the console always dry-runs first. The dry run
   * is the **real** transaction rolled back, so its counts are what applying it
   * would do rather than what a simulator thinks it would do.
   */
  seedCatalog(tenantId: string, file: string, apply = false): Promise<CatalogSeeded> {
    return this.client.request({
      method: 'POST',
      path: `/tenants/${encodeURIComponent(tenantId)}/catalog/seed`,
      body: { file, apply },
      schema: CatalogSeeded,
    });
  }

  /* ---- options and add-ons (migration 0048, W5c) ----------------- */

  /**
   * Add a way this service is sold — a length, a size.
   *
   * The **first** option an item gets becomes its headline: the item's own
   * `price_cents` and `duration_min` are restated from the default option, in
   * the same transaction, by `packages/store`. That is why all four of these
   * answer with the item as it now is rather than with a version alone — the
   * console repaints the row from the server's answer instead of from what it
   * hoped the server did.
   *
   * `name` is a plain string in the tenant's default locale, or — since the
   * editor grew its second-language box (L8) — a map of language → words, the
   * item name's own spelling. A map replaces the stored map; the console sends
   * one only when the second box has words in it.
   */
  createOption(
    tenantId: string,
    itemId: string,
    body: {
      ref: string;
      name: string | Record<string, string>;
      price_cents: number;
      /** `null` says this option does not change the span (a dish's *Large*). */
      duration_min?: number | null;
      is_default?: boolean;
      active?: boolean;
      sort_order?: number;
    },
  ): Promise<CatalogOptionChanged> {
    return this.client.request({
      method: 'POST',
      path: `/tenants/${encodeURIComponent(tenantId)}/catalog/items/${encodeURIComponent(itemId)}/options`,
      body,
      schema: CatalogOptionChanged,
    });
  }

  /**
   * Rename, reprice, retime, reorder, mark the default, or **retire** one
   * option.
   *
   * There is deliberately no delete and there will not be one:
   * `appointments.option_id` is `ON DELETE RESTRICT`, so a length somebody was
   * sold still has to appear on last March's report. `active: false` is how a
   * length stops being offered, and the default falls through to the first live
   * one rather than pricing the item at something nobody can book.
   *
   * The path names the option and not its item, because an option id is already
   * unique — the server answers with the item it belongs to.
   */
  updateOption(
    tenantId: string,
    optionId: string,
    body: {
      ref?: string;
      name?: string | Record<string, string>;
      price_cents?: number;
      duration_min?: number | null;
      is_default?: boolean;
      active?: boolean;
      sort_order?: number;
    },
  ): Promise<CatalogOptionChanged> {
    return this.client.request({
      method: 'PATCH',
      path: `/tenants/${encodeURIComponent(tenantId)}/catalog/options/${encodeURIComponent(optionId)}`,
      body,
      schema: CatalogOptionChanged,
    });
  }

  /**
   * Link an add-on to a service, or restate what it costs attached.
   *
   * One verb for both, because "we sell dermaplaning on a facial at $45" is one
   * statement whether or not it was true yesterday. Both numbers are optional
   * and an absent one means **the same as standing alone**, which is not the
   * same as zero — and both may legitimately be *lower* attached than alone,
   * which is why a link carries its own price rather than a delta.
   */
  setAddOn(
    tenantId: string,
    itemId: string,
    body: {
      add_on_item_id: string;
      price_cents?: number | null;
      duration_min?: number | null;
      sort_order?: number;
      active?: boolean;
    },
  ): Promise<CatalogOptionChanged> {
    return this.client.request({
      method: 'PUT',
      path: `/tenants/${encodeURIComponent(tenantId)}/catalog/items/${encodeURIComponent(itemId)}/add-ons`,
      body,
      schema: CatalogOptionChanged,
    });
  }

  /**
   * Unlink an add-on — the one destructive verb on the catalog surface.
   *
   * It is allowed where an option's delete is not because nothing in the book
   * points at a *pairing*: `appointment_services` snapshots the add-on's name,
   * price and minutes at the moment it was sold.
   */
  removeAddOn(
    tenantId: string,
    itemId: string,
    addOnItemId: string,
  ): Promise<CatalogOptionChanged> {
    return this.client.request({
      method: 'DELETE',
      path: `/tenants/${encodeURIComponent(tenantId)}/catalog/items/${encodeURIComponent(itemId)}/add-ons/${encodeURIComponent(addOnItemId)}`,
      schema: CatalogOptionChanged,
    });
  }

  /* ---- a photograph from the operator's computer (B7) ------------- */

  /** A signed PUT for one photograph. HEIC and files over 10 MB are refused by name. */
  catalogPhotoUploadUrl(
    tenantId: string,
    itemId: string,
    body: { content_type: string; bytes: number; file_name?: string },
  ): Promise<CatalogUploadMinted> {
    return this.client.request({
      method: 'POST',
      path: `/tenants/${encodeURIComponent(tenantId)}/catalog/items/${encodeURIComponent(itemId)}/media/upload-url`,
      body,
      schema: CatalogUploadMinted,
    });
  }

  /** Check what landed and copy it where a page can show it; answers the address. */
  publishCatalogPhoto(
    tenantId: string,
    itemId: string,
    uploadId: string,
    contentType: string,
  ): Promise<CatalogUploadPublished> {
    return this.client.request({
      method: 'POST',
      path: `/tenants/${encodeURIComponent(tenantId)}/catalog/items/${encodeURIComponent(itemId)}/media/uploads/${encodeURIComponent(uploadId)}/publish`,
      body: { content_type: contentType },
      schema: CatalogUploadPublished,
    });
  }

  /**
   * The whole of an upload: mint, PUT the bytes, publish. Answers the public
   * address; **attaches nothing** — the photographs editor's Save does that,
   * with the alt text it requires.
   *
   * The PUT goes to the bucket, not to `admin-api`, so it carries no token and
   * no JSON; its failures are said in the console's own words, because the
   * bucket has none an operator could use.
   */
  async uploadCatalogPhoto(tenantId: string, itemId: string, file: File): Promise<string> {
    const minted = await this.catalogPhotoUploadUrl(tenantId, itemId, {
      content_type: file.type,
      bytes: file.size,
      file_name: file.name.slice(0, 200),
    });
    let res: Response;
    try {
      res = await this.putImpl(minted.upload_url, {
        method: 'PUT',
        headers: minted.headers,
        body: file,
      });
    } catch (e) {
      throw ApiFailure.offline(String(e));
    }
    if (!res.ok) {
      throw new ApiFailure(
        'unavailable',
        `The photograph's upload answered ${String(res.status)}.`,
        {
          status: res.status,
          own: { key: 'failure.http', params: { status: res.status } },
        },
      );
    }
    const published = await this.publishCatalogPhoto(
      tenantId,
      itemId,
      minted.upload_id,
      minted.headers['content-type'] ?? file.type,
    );
    return published.url;
  }

  executions(job: JobKind, limit = 20): Promise<ExecutionList> {
    return this.client.request({
      method: 'GET',
      path: `/jobs/${job}/executions`,
      query: { limit },
      schema: ExecutionList,
    });
  }

  execution(job: JobKind, executionId: string): Promise<ExecutionDetail> {
    return this.client.request({
      method: 'GET',
      path: `/jobs/${job}/executions/${encodeURIComponent(executionId)}`,
      schema: ExecutionDetail,
    });
  }

  /** Where the business is in getting live — the Overview checklist's facts (B1). */
  tenantReadiness(tenantId: string): Promise<TenantReadiness> {
    return this.client.request({
      method: 'GET',
      path: `/tenants/${encodeURIComponent(tenantId)}/readiness`,
      schema: TenantReadiness,
    });
  }

  /* ---- their app's look (B8) -------------------------------------- */

  /** The branding read, with whether pictures can be uploaded on this deployment. */
  appLook(tenantId: string): Promise<AppLookRead> {
    return this.client.request({
      method: 'GET',
      path: `/tenants/${encodeURIComponent(tenantId)}/branding`,
      schema: AppLookRead,
    });
  }

  /**
   * Save the staff app's named set **alone** — the website's document and its
   * version are not sent and not touched. `null` puts the app back to how it
   * ships.
   */
  saveAppLook(tenantId: string, look: Record<string, string> | null): Promise<AppLookSaved> {
    return this.client.request({
      method: 'PUT',
      path: `/tenants/${encodeURIComponent(tenantId)}/branding/app-look`,
      body: { branding_staff: look },
      schema: AppLookSaved,
    });
  }

  /**
   * The whole of a business-level picture upload: mint, PUT the bytes,
   * publish. Answers the public address and **attaches nothing** — the look's
   * save does that. The catalog photo upload's shape, keyed by the business.
   */
  async uploadTenantAsset(tenantId: string, kind: 'staff-background', file: File): Promise<string> {
    const base = `/tenants/${encodeURIComponent(tenantId)}/assets`;
    const minted = await this.client.request({
      method: 'POST',
      path: `${base}/upload-url`,
      body: {
        kind,
        content_type: file.type,
        bytes: file.size,
        file_name: file.name.slice(0, 200),
      },
      schema: TenantAssetMinted,
    });
    let res: Response;
    try {
      res = await this.putImpl(minted.upload_url, {
        method: 'PUT',
        headers: minted.headers,
        body: file,
      });
    } catch (e) {
      throw ApiFailure.offline(String(e));
    }
    if (!res.ok) {
      throw new ApiFailure('unavailable', `The picture's upload answered ${String(res.status)}.`, {
        status: res.status,
        own: { key: 'failure.http', params: { status: res.status } },
      });
    }
    const published = await this.client.request({
      method: 'POST',
      path: `${base}/uploads/${encodeURIComponent(minted.upload_id)}/publish`,
      body: { kind, content_type: minted.headers['content-type'] ?? file.type },
      schema: TenantAssetPublished,
    });
    return published.url;
  }

  /* ---- who can sign in: a business's staff logins ------------------ */

  /** The record and Identity Platform side by side, with what they disagree about. */
  logins(tenantId: string): Promise<LoginList> {
    return this.client.request({
      method: 'GET',
      path: `/tenants/${encodeURIComponent(tenantId)}/logins`,
      schema: LoginList,
    });
  }

  /** Make a login with no password; Google mails the set-your-password link itself. */
  inviteLogin(
    tenantId: string,
    body: { email: string; role: LoginRole; name?: string },
  ): Promise<LoginInvited> {
    return this.client.request({
      method: 'POST',
      path: `/tenants/${encodeURIComponent(tenantId)}/logins`,
      body,
      schema: LoginInvited,
    });
  }

  resendLogin(tenantId: string, uid: string): Promise<LoginChanged> {
    return this.loginVerb(tenantId, uid, 'POST', 'resend');
  }

  setLoginRole(tenantId: string, uid: string, role: LoginRole): Promise<LoginChanged> {
    return this.loginVerb(tenantId, uid, 'PUT', 'role', { role });
  }

  revokeLogin(tenantId: string, uid: string): Promise<LoginChanged> {
    return this.loginVerb(tenantId, uid, 'POST', 'revoke');
  }

  recordLogin(tenantId: string, uid: string): Promise<LoginChanged> {
    return this.loginVerb(tenantId, uid, 'POST', 'record');
  }

  private loginVerb(
    tenantId: string,
    uid: string,
    method: 'POST' | 'PUT',
    verb: 'resend' | 'role' | 'revoke' | 'record',
    body: Record<string, unknown> = {},
  ): Promise<LoginChanged> {
    return this.client.request({
      method,
      path: `/tenants/${encodeURIComponent(tenantId)}/logins/${encodeURIComponent(uid)}/${verb}`,
      body,
      schema: LoginChanged,
    });
  }
}
